import * as React from "react";
import { Icon } from "@iconify/react";
import { OrderActionBar, OrderActionMenuItem } from "./OrderActionBar";
import { OrderResponse } from "@/types/order-type";


type OrderStatus =
    | "DRAFT"
    | "CONFIRMED"
    | "READY_FOR_DELIVERY"
    | "DELIVERY"
    | "DELIVERED"
    | "CANCELLED";

type DeliveryStatus =
    | "NOT_PACKED"
    | "READY_FOR_DELIVERY"
    | "DELIVERY"
    | "DELIVERED";

type PaymentStatus = "PENDING" | "PARTIALLY_PAID" | "PAID" | "CAPTURED" | "REFUNDED" | "FAILED";

type PaymentMethod = "CASH" | "CARD" | "COD" | "BANK_TRANSFER" | "OTHER";


type Props = {
    order: OrderResponse;

    // Document actions
    onDownloadInvoice?: () => void;
    onPrintInvoice?: () => void;
    onShowReceipt?: () => void;

    // Order lifecycle actions
    onConfirmOrder: () => void;
    onMarkAsCOD: () => void;
    onCapturePayment: () => void;
    onCancelOrder: () => void;
    onMarkReadyForDelivery: () => void;
    onMarkDelivered: () => void;
    onRefundOrder: () => void;
    onReturnOrder: () => void;
    onAssignToUser?: () => void;
};

export function OrderHeaderActions({
    order,
    onDownloadInvoice,
    onPrintInvoice,
    onShowReceipt,
    onConfirmOrder,
    onMarkAsCOD,
    onCapturePayment,
    onCancelOrder,
    onMarkReadyForDelivery,
    onMarkDelivered,
    onRefundOrder,
    onReturnOrder,
    onAssignToUser,
}: Props) {
    const isDraft = order.status === "DRAFT";
    const isConfirmed = order.status === "CONFIRMED" || order.status == "PAID";
    const isReadyForDelivery = order.deliveryStatus === "READY_FOR_DELIVERY";
    const isDelivered = order.deliveryStatus === "DELIVERED";

    const isPaid =
        order.paymentStatus === "PAID" || order.paymentStatus === "CAPTURED";
    const isCOD =
        order.paymentMethod === "COD" || order.paymentMethod === "CASH";

    // You can only change payment if not yet paid/COD
    const canChangePayment = isConfirmed && !isPaid && !isCOD;

    // Once paid or COD, you can mark ready
    const canMarkReady =
        isConfirmed && (isPaid || isCOD) && !isReadyForDelivery && !isDelivered;

    const canMarkDelivered = isReadyForDelivery;
    const canRefundOrReturn = isDelivered;

    const menuItems: OrderActionMenuItem[] = [];

    // 1) DRAFT → Confirm only
    if (isDraft) {
        menuItems.push({
            label: "Confirm Order",
            icon: (
                <Icon
                    icon="mdi:check-circle-outline"
                    className="h-5 w-5 text-emerald-500"
                />
            ),
            onClick: onConfirmOrder,
        });
    }

    // 2) CONFIRMED → COD / Capture / Cancel
    if (isConfirmed) {
        if (canChangePayment) {
            menuItems.push(
                {
                    label: "Mark as Cash on Delivery",
                    icon: (
                        <Icon icon="mdi:cash" className="h-5 w-5 text-emerald-500" />
                    ),
                    onClick: onMarkAsCOD,
                },
                {
                    label: "Capture Payment",
                    icon: (
                        <Icon
                            icon="mdi:credit-card-check-outline"
                            className="h-5 w-5 text-green-500"
                        />
                    ),
                    onClick: onCapturePayment,
                }
            );
        }

        menuItems.push({
            label: "Cancel Order",
            icon: (
                <Icon
                    icon="mdi:close-octagon-outline"
                    className="h-5 w-5 text-red-500"
                />
            ),
            onClick: onCancelOrder,
            danger: true,
            disabled: isPaid || isCOD, // prevent cancel if already paid/COD (optional)
        });
    }

    // 3) PAID or COD → Ready for delivery
    if (canMarkReady) {
        menuItems.push({
            label: "Mark as Ready for Delivery",
            icon: (
                <Icon
                    icon="mdi:truck-check-outline"
                    className="h-5 w-5 text-indigo-500"
                />
            ),
            onClick: onMarkReadyForDelivery,
        });
    }

    // 4) READY_FOR_DELIVERY → Delivered
    if (canMarkDelivered) {
        menuItems.push({
            label: "Mark as Delivered",
            icon: (
                <Icon
                    icon="mdi:truck-delivery-outline"
                    className="h-5 w-5 text-indigo-600"
                />
            ),
            onClick: onMarkDelivered,
        });
    }

    // 5) DELIVERED → Refund / Return
    if (canRefundOrReturn) {
        menuItems.push(
            {
                label: "Refund Order",
                icon: (
                    <Icon icon="mdi:cash-refund" className="h-5 w-5 text-amber-500" />
                ),
                onClick: onRefundOrder,
                danger: true,
            },
            {
                label: "Return Order",
                icon: (
                    <Icon
                        icon="mdi:package-down"
                        className="h-5 w-5 text-amber-600"
                    />
                ),
                onClick: onReturnOrder,
                danger: true,
            }
        );
    }
    menuItems.push({
        label: "Assign To User",
        icon: (
            <Icon icon="mdi:account-multiple-outline" className="h-5 w-5 text-indigo-500" />
        ),
        onClick: onAssignToUser || (() => { }),
    });

    return (
        <OrderActionBar
            onDownloadInvoice={onDownloadInvoice}
            onPrintInvoice={onPrintInvoice}
            onShowReceipt={onShowReceipt}
            menuItems={menuItems}
            className="ml-auto" // align right if used in header flex
        />
    );
}